/**
 * The player drawer: summary, punishment timeline, alts, recent activity.
 *
 * It subscribes to the live feed too. If somebody is banned while staff have this open, the timeline
 * gains the ban without a refresh — which is the case that actually matters, because the drawer is
 * what is open while a punishment is being decided.
 *
 * Alts are behind a click rather than loaded with the rest. Fetching them writes an audit row naming
 * the staff member and the player, and an audit log that fills up every time a drawer is opened tells
 * you nothing. Behind a button, every row in it means somebody chose to look.
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NotAuthorizedError, fetchAlts, fetchPlayerSummary, fetchPunishments } from "../api";
import { formatDuration, formatRelative, formatTimestamp } from "../format";
import type { AltsResponse, PunishmentEvent, Tier } from "../types";

interface Props {
  player: string;
  tier: Tier;
  onClose: () => void;
  onOpenPlayer: (player: string) => void;
  /** Punishment events from the shared stream, already filtered to this player by the server. */
  lastPunishmentEvent: PunishmentEvent | null;
}

export function PlayerDrawer({
  player,
  tier,
  onClose,
  onOpenPlayer,
  lastPunishmentEvent,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [alts, setAlts] = useState<AltsResponse | null>(null);
  const [altsError, setAltsError] = useState<string | null>(null);
  const [altsLoading, setAltsLoading] = useState(false);

  const summary = useQuery({
    queryKey: ["summary", player],
    queryFn: () => fetchPlayerSummary(player),
  });

  const punishments = useQuery({
    queryKey: ["punishments", player],
    queryFn: () => fetchPunishments(player),
  });

  // A punishment landed for this player while the drawer was open. Refetching rather than splicing
  // the event into the list: the server reconciles the current mute against mute_log, and duplicating
  // that logic here to save one query is how the two ends start disagreeing.
  useEffect(() => {
    if (!lastPunishmentEvent) return;
    void queryClient.invalidateQueries({ queryKey: ["punishments", player] });
    void queryClient.invalidateQueries({ queryKey: ["summary", player] });
  }, [lastPunishmentEvent, player, queryClient]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Reset alt state when the drawer switches players, so one player's alts cannot be shown under
  // another's name for a frame.
  useEffect(() => {
    setAlts(null);
    setAltsError(null);
  }, [player]);

  const loadAlts = async (): Promise<void> => {
    setAltsLoading(true);
    setAltsError(null);
    try {
      setAlts(await fetchAlts(player));
    } catch (error) {
      setAltsError(
        error instanceof NotAuthorizedError
          ? "Your role does not include alt lookups."
          : "Could not load alts.",
      );
    } finally {
      setAltsLoading(false);
    }
  };

  const data = summary.data;

  return (
    <aside className="drawer" aria-label={`Player details for ${player}`}>
      <div className="drawer-head">
        <h2>{data?.player ?? player}</h2>
        <span className="grow" />
        <button type="button" className="btn" onClick={onClose} aria-label="Close (Esc)">
          Close
        </button>
      </div>

      {summary.isError && (
        <section>
          <div className="note" data-k="danger">Could not load this player.</div>
        </section>
      )}

      {data && (
        <>
          <section>
            {data.currentBan && (
              <div className="note" data-k="danger">
                <strong>Currently banned</strong> — {data.currentBan.reason}
                <div className="pun meta">
                  by {data.currentBan.issuer} · {formatDuration(data.currentBan.expires_at)}
                  {data.currentBan.ip_ban && " · IP-scoped"}
                </div>
              </div>
            )}
            {data.currentMute && (
              <div className="note" data-k="warn">
                <strong>Currently muted</strong> — {data.currentMute.reason}
                <div className="pun meta">
                  by {data.currentMute.issuer} · {formatDuration(data.currentMute.expires_at)}
                </div>
              </div>
            )}

            <div className="stats">
              <div className="stat">
                <div className="n">{data.banCount}</div>
                <div className="l">bans</div>
              </div>
              <div className="stat">
                <div className="n">{data.muteCount}</div>
                <div className="l">mutes</div>
              </div>
              <div className="stat">
                <div className="n">
                  {data.kickCount}
                  {!data.kickHistoryAvailable && <span style={{ fontSize: 12 }}>+</span>}
                </div>
                <div className="l">kicks</div>
              </div>
              <div className="stat">
                <div className="n">{data.altsVisible ? data.altCount : "—"}</div>
                <div className="l">alts</div>
              </div>
            </div>

            {!data.kickHistoryAvailable && (
              <div className="note" style={{ marginTop: 10 }}>
                Kick history is unavailable on this server: the <code>kicks</code> table keeps only
                the most recent kick per player. The count above is a floor, not a total.
              </div>
            )}

            <div className="pun meta" style={{ marginTop: 10 }}>
              First seen {formatRelative(data.firstSeen)} · last seen {formatRelative(data.lastSeen)}
              {data.uuid && (
                <>
                  <br />
                  <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>{data.uuid}</span>
                </>
              )}
            </div>
          </section>

          <section>
            <h3>Punishment history</h3>
            {punishments.data?.punishments.length === 0 && (
              <div className="pun meta">Nothing on record.</div>
            )}
            <div className="timeline">
              {punishments.data?.punishments.map((entry, index) => (
                <div key={`${entry.kind}-${entry.id ?? index}`} className="pun" data-kind={entry.kind}>
                  <div className="head">
                    <span className="kind">{entry.kind}</span>
                    <span className="when">{formatTimestamp(entry.issued_at)}</span>
                    {entry.active && <span className="tag" data-k="active">active</span>}
                  </div>
                  <div>{entry.reason}</div>
                  <div className="meta">
                    by {entry.issuer}
                    {entry.kind !== "kick" && ` · ${formatDuration(entry.expires_at)}`}
                    {entry.ip_ban && " · IP-scoped"}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3>Known alts</h3>
            {tier === "viewer" ? (
              <div className="pun meta">
                {data.altCount > 0
                  ? "This account shares an address with others. Alt names are visible to staff and above."
                  : "Alt details are visible to staff and above."}
              </div>
            ) : alts ? (
              <>
                {alts.limited && (
                  <div className="note">
                    Answered from <code>ip_log</code>, which keeps only each account&rsquo;s current
                    address and purges after a week. This is &ldquo;who shares this address right
                    now&rdquo;, not full history.
                  </div>
                )}
                {alts.accounts.length <= 1 ? (
                  <div className="pun meta">No other accounts on this address.</div>
                ) : (
                  <div className="timeline">
                    {alts.accounts
                      .filter((account) => account.player.toLowerCase() !== player.toLowerCase())
                      .map((account) => (
                        <div key={account.player} className="pun">
                          <button
                            type="button"
                            className="who"
                            onClick={() => onOpenPlayer(account.player)}
                          >
                            {account.player}
                          </button>
                          <div className="meta">last seen {formatRelative(account.lastSeen)}</div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <button type="button" className="btn" onClick={loadAlts} disabled={altsLoading}>
                  {altsLoading ? "loading…" : "Reveal alts"}
                </button>
                <div className="pun meta" style={{ marginTop: 6 }}>
                  Recorded in the access audit log.
                </div>
                {altsError && <div className="note" data-k="danger">{altsError}</div>}
              </>
            )}
          </section>
        </>
      )}
    </aside>
  );
}
